// Grid 上のマスタ検索条件補正をまとめます。
export function createGridMasterSearchService(deps) {
    const {
        entrydataRef,
        isEmpty,
        DicVal,
        normalizeKakousijiUiString,
        readRowValueIgnoreCase,
        getFukushizaiColumnInfoById,
        isSijisuPdtcMasterColumn,
        applySijisuPdtcMasterSearchParamToColInfo,
        isDemeritPanelInfo,
        isBrandNamePanelInfo,
        resolveFukushizaiSupplierCodeForRow,
        DEMERIT_NAME_DMY,
        DEMERIT_HUDA_DMY
    } = deps;

    // searchparam を安全に複製します。
    function cloneSearchParam(searchparam) {
        if (isEmpty(searchparam)) return {};
        if (typeof searchparam === "object" && !Array.isArray(searchparam)) return { ...searchparam };
        return { requirement: normalizeKakousijiUiString(searchparam) };
    }

    // SQL 条件へ埋め込む文字列をエスケープします。
    function escapeSqlLiteral(value) {
        return normalizeKakousijiUiString(value).replace(/'/g, "''");
    }

    // デメリット検索条件で使う値を row 優先で取得します。
    function readDemeritMasterConditionValue(row, fieldId) {
        const fromRow = normalizeKakousijiUiString(readRowValueIgnoreCase(row, fieldId));
        if (fromRow !== "") return fromRow;
        return normalizeKakousijiUiString(readRowValueIgnoreCase(entrydataRef.current, fieldId));
    }

    // デメリットの絞り込みに使う仕入先を返します。
    //
    // 商品情報の仕入先(SSD_SIIRECD)ではなく副資材の仕入先(SSDZ_TORCODE)を使います。
    // 副資材の仕入先はアイテム＋グループごとに異なるため、対象行の所属グループで解決します。
    function readDemeritSupplierCode(row) {
        if (typeof resolveFukushizaiSupplierCodeForRow === "function") {
            const resolved = normalizeKakousijiUiString(resolveFukushizaiSupplierCodeForRow(row));
            if (resolved !== "") return resolved;
        }
        return readDemeritMasterConditionValue(row, "SSDZ_TORCODE");
    }

    // デメリット検索条件に埋め込んだ動的条件を取り除きます。
    function stripDemeritDynamicConditionText(text) {
        return normalizeKakousijiUiString(text)
            .replace(/(?:^|\s+AND\s+)FZK_TORCODE\s*=\s*'[^']*'/ig, "")
            .replace(/(?:^|\s+AND\s+)FZK_BRDCODE\s*=\s*'[^']*'/ig, "")
            .replace(/(?:^|\s+AND\s+)FZK_TORCODE\s*=\s*[^;\s)]+/ig, "")
            .replace(/(?:^|\s+AND\s+)FZK_BRDCODE\s*=\s*[^;\s)]+/ig, "")
            // 過去に付与された絞り込み無効化条件も除去する。
            // 残したまま再構築すると条件が積み上がり、以降ずっと該当0件になる。
            .replace(/(?:^|\s+AND\s+)1\s*=\s*0/ig, "")
            .replace(/__KAKOUSIJI_[A-Z0-9_]+__/g, "")
            .replace(/^\s*AND\s+/i, "")
            .replace(/\s+AND\s*$/i, "")
            .trim();
    }

    // デメリット検索条件の固定部分だけを抽出します。
    function normalizeDemeritBaseRequirement(requirement) {
        const chunks = normalizeKakousijiUiString(requirement)
            .split(";")
            .map((part) => part.trim())
            .filter((part) => part !== "")
            .map((part) => part.replace(/^where=/i, "").trim())
            .map((part) => stripDemeritDynamicConditionText(part))
            .filter((part) => part !== "")
            .filter((part) => !/^FZK_TORCODE\s*=/i.test(part))
            .filter((part) => !/^FZK_BRDCODE\s*=/i.test(part))
            .filter((part) => !/^1\s*=\s*0$/i.test(part))
            .filter((part) => !/__KAKOUSIJI_[A-Z0-9_]+__/.test(part));
        return chunks.length === 0 ? "1=1" : chunks.join(" AND ");
    }

    // デメリット検索条件へ加工指示依存の実値を追加します。
    function buildDemeritMasterSearchParam(sourceSearchParam, row = null) {
        const next = cloneSearchParam(sourceSearchParam);
        if (typeof next !== "object" || Array.isArray(next)) return next;
        const torCode = escapeSqlLiteral(readDemeritSupplierCode(row));
        const brandCode = escapeSqlLiteral(readDemeritMasterConditionValue(row, "PDT_BRDCODE"));
        const baseWhere = normalizeDemeritBaseRequirement(DicVal(next, "requirement", ""));
        const conditions = [baseWhere];
        // 値が取れない条件は付けない。空文字で条件を組むと該当0件になり、候補が出せなくなる。
        if (torCode !== "") conditions.push(`FZK_TORCODE='${torCode}'`);
        if (brandCode !== "") conditions.push(`FZK_BRDCODE='${brandCode}'`);
        next.requirement = `where=${conditions.join(" AND ")}`;
        return next;
    }

    // デメリット列定義へ検索条件補正を反映します。
    function applyDemeritMasterSearchParamToColInfo(colInfo, row = null) {
        if (isEmpty(colInfo)) return colInfo;
        const original = getFukushizaiColumnInfoById(DicVal(colInfo, "columnid", ""));
        const baseSearchParam = DicVal(original, "searchparam", DicVal(colInfo, "searchparam", null));
        return {
            ...colInfo,
            searchparam: buildDemeritMasterSearchParam(baseSearchParam, row)
        };
    }

    // 素材品番 master 列かどうかを判定します。
    function isSozaiZaiHinbanMasterColumn(panelInfo, colDef) {
        const panelType = String(DicVal(panelInfo, "type", DicVal(panelInfo, "panelType", "")) ?? "").trim();
        const colInfo = DicVal(colDef, "colinfo", {});
        const columnId = String(DicVal(colInfo, "columnid", DicVal(colDef, "colId", DicVal(colDef, "field", ""))) ?? "").trim().toUpperCase();
        const table = String(DicVal(colInfo, "table", "") ?? "").trim().toUpperCase();
        const masterType = String(DicVal(colInfo, "mastertype", DicVal(colDef, "mastertype", DicVal(colInfo, "dataid", DicVal(colDef, "dataid", "")))) ?? "").trim().toUpperCase();
        const dataId = String(DicVal(colInfo, "dataid", DicVal(colDef, "dataid", "")) ?? "").trim().toUpperCase();
        const matchesTable = table === "" || table === "D_SSDS";
        const matchesMaster = (masterType === "" && dataId === "") || masterType === "M_ZAI" || dataId === "M_ZAI";
        return panelType === "107"
            && columnId === "SSDS_ZAIHINBAN"
            && matchesTable
            && matchesMaster;
    }

    // 素材品番検索条件で使うブランドコードを取得します。
    function readSozaiZaiHinbanBrandCode(row) {
        const fromEntry = normalizeKakousijiUiString(readRowValueIgnoreCase(entrydataRef.current, "PDT_BRDCODE"));
        if (fromEntry !== "") return fromEntry;
        return normalizeKakousijiUiString(readRowValueIgnoreCase(row, "PDT_BRDCODE"));
    }

    // 素材品番検索条件に埋め込んだ動的条件を取り除きます。
    function stripSozaiZaiHinbanDynamicConditionText(text) {
        return normalizeKakousijiUiString(text)
            .replace(/(?:^|\s+AND\s+)ZAI_BRDCODE\s*=\s*'[^']*'/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_TORKUBUN\s*=\s*'[^']*'/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_TORCODE\s*=\s*'[^']*'/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_TORSUBCODE\s*=\s*'[^']*'/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_INVALID\s*=\s*'[^']*'/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_INVALID\s*=\s*[01]/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_BRDCODE\s*=\s*[^;\s)]+/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_TORKUBUN\s*=\s*[^;\s)]+/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_TORCODE\s*=\s*[^;\s)]+/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_TORSUBCODE\s*=\s*[^;\s)]+/ig, "")
            .replace(/(?:^|\s+AND\s+)ZAI_INVALID\s*=\s*[^;\s)]+/ig, "")
            .replace(/^\s*AND\s+/i, "")
            .replace(/\s+AND\s*$/i, "")
            .trim();
    }

    // 素材品番検索条件の固定部分だけを抽出します。
    function normalizeSozaiZaiHinbanBaseRequirement(requirement) {
        const chunks = normalizeKakousijiUiString(requirement)
            .split(";")
            .map((part) => part.trim())
            .filter((part) => part !== "")
            .map((part) => part.replace(/^where=/i, "").trim())
            .map((part) => stripSozaiZaiHinbanDynamicConditionText(part))
            .filter((part) => part !== "")
            .filter((part) => !/^ZAI_BRDCODE\s*=/i.test(part))
            .filter((part) => !/^ZAI_TORKUBUN\s*=/i.test(part))
            .filter((part) => !/^ZAI_TORCODE\s*=/i.test(part))
            .filter((part) => !/^ZAI_TORSUBCODE\s*=/i.test(part))
            .filter((part) => !/^ZAI_INVALID\s*=/i.test(part));
        return chunks.length === 0 ? "1=1" : chunks.join(" AND ");
    }

    // 素材品番検索条件へ加工指示依存の実値を追加します。
    function buildSozaiZaiHinbanMasterSearchParam(sourceSearchParam, row = null) {
        const next = cloneSearchParam(sourceSearchParam);
        if (typeof next !== "object" || Array.isArray(next)) return next;
        const baseWhere = normalizeSozaiZaiHinbanBaseRequirement(DicVal(next, "requirement", ""));
        const brandCode = escapeSqlLiteral(readSozaiZaiHinbanBrandCode(row));
        const torKubun = escapeSqlLiteral(normalizeKakousijiUiString(readRowValueIgnoreCase(row, "SSDS_SIIREKBN")) || "1");
        const torCode = escapeSqlLiteral(readRowValueIgnoreCase(row, "SSDS_SIIRECD"));
        const torSubCode = escapeSqlLiteral(normalizeKakousijiUiString(readRowValueIgnoreCase(row, "SSDS_SIIRESUBCD")) || "00000");
        if (brandCode === "" || torCode === "") {
            next.requirement = `where=${baseWhere} AND 1=0`;
            return next;
        }
        next.requirement = `where=${baseWhere} AND ZAI_BRDCODE='${brandCode}' AND ZAI_TORKUBUN='${torKubun}' AND ZAI_TORCODE='${torCode}' AND ZAI_TORSUBCODE='${torSubCode}' AND ZAI_INVALID=0`;
        return next;
    }

    // 素材品番列定義へ検索条件補正を反映します。
    function applySozaiZaiHinbanMasterSearchParamToColInfo(colInfo, row = null) {
        if (isEmpty(colInfo)) return colInfo;
        const original = getFukushizaiColumnInfoById(DicVal(colInfo, "columnid", ""));
        const baseSearchParam = DicVal(original, "searchparam", DicVal(colInfo, "searchparam", null));
        return {
            ...colInfo,
            searchparam: buildSozaiZaiHinbanMasterSearchParam(baseSearchParam, row)
        };
    }

    // ブランドネーム Grid クリック時の選択補正を行います。
    function handleBrandNameGridClick(param) {
        const colId = String(DicVal(param, "colDef", {})?.colId ?? DicVal(param, "colDef", {})?.field ?? "").trim().toUpperCase();
        if (colId !== "BRANDNAME_CD_DMY") return false;
        if (typeof param.api?.deselectAll === "function") {
            param.api.deselectAll();
        }
        if (typeof param.node?.setSelected === "function") {
            param.node.setSelected(true);
        }
        return true;
    }

    // デメリット Grid クリック時の検索条件補正を行います。
    function handleDemeritGridClick(panel, param) {
        if (!isDemeritPanelInfo(panel)) return false;
        const colId = String(DicVal(param, "colDef", {})?.colId ?? DicVal(param, "colDef", {})?.field ?? "").trim().toUpperCase();
        if (colId !== DEMERIT_NAME_DMY.toUpperCase() && colId !== DEMERIT_HUDA_DMY.toUpperCase()) return false;
        param.colDef.colinfo = applyDemeritMasterSearchParamToColInfo(param.colDef.colinfo, param.data);
        param.colDef.searchparam = DicVal(param.colDef.colinfo, "searchparam", param.colDef.searchparam);
        return true;
    }

    // panel ごとの Grid クリック補正をまとめて適用します。
    function handlePanelGridCellClicked(panel, param) {
        if (isEmpty(param) || isEmpty(param.colDef) || isEmpty(param.node)) return;
        if (isSozaiZaiHinbanMasterColumn(panel, param.colDef)) {
            param.colDef.colinfo = applySozaiZaiHinbanMasterSearchParamToColInfo(param.colDef.colinfo, param.data);
            param.colDef.searchparam = DicVal(param.colDef.colinfo, "searchparam", param.colDef.searchparam);
            return;
        }
        if (isSijisuPdtcMasterColumn(panel, param.colDef)) {
            param.colDef.colinfo = applySijisuPdtcMasterSearchParamToColInfo(param.colDef.colinfo, param.data);
            param.colDef.searchparam = DicVal(param.colDef.colinfo, "searchparam", param.colDef.searchparam);
            return;
        }
        if (handleDemeritGridClick(panel, param)) return;
        if (!isBrandNamePanelInfo(panel)) return;
        handleBrandNameGridClick(param);
    }

    return {
        buildDemeritMasterSearchParam,
        handlePanelGridCellClicked
    };
}
